import { Link } from "react-router-dom";
import { PlusIcon } from "lucide-react";

import {
  useReactivateUser,
  useSuspendUser,
  useUpdateUser,
  useUsers,
} from "@/api/users";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExportCsvButton } from "@/components/ExportCsvButton";
import { Card, CardContent } from "@/components/ui/card";
import { useTenantStore } from "@/store/tenant";
import type { Role, UserDetail } from "@/types";

// The fixed legacy users.role enum (DB type user_role) — the base access level.
// This is NOT the tenant-role list; per-entity role GRANTS come from GET /roles
// and are managed on the user detail page.
const LEGACY_ROLES: Role[] = ["admin", "operator", "viewer", "auditor"];

function UserRow({ u }: { u: UserDetail }) {
  const update = useUpdateUser(u.id);
  const suspend = useSuspendUser();
  const reactivate = useReactivateUser();
  const busy = update.isPending || suspend.isPending || reactivate.isPending;

  return (
    <tr className="border-b last:border-0">
      <td className="px-4 py-3">
        <Link to={`/users/${u.id}`} className="hover:underline">
          {u.full_name ?? "—"}
        </Link>
      </td>
      <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
      <td className="px-4 py-3">
        {/* Edit base role → PATCH /users/{id}. */}
        <select
          value={u.role}
          disabled={busy}
          onChange={(e) => update.mutate({ role: e.target.value as Role })}
          // Reported as rendering all four options at once. Not reproducible
          // without a browser; an unconstrained height is the usual cause of a
          // select rendering as a list box rather than a collapsed dropdown, so
          // h-9 is the most defensible change available. A likely fix, not a
          // proven one — confirm on the browser pass.
          //
          // CORRECTION 2026-08-07: this comment used to claim it was "the ONLY
          // <select> in the app carrying no height class (27 others use
          // h-9/h-10)". That was WRONG — measured today, 36 of 70 selects carry
          // no h-* class, including the sibling role selects in
          // Users/UserDetail and Users/UserInvite, which are the SAME control.
          // Fixing one of three and calling it done is how this codebase keeps
          // shipping the same fix repeatedly; all three now carry h-9.
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
        >
          {LEGACY_ROLES.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </td>
      <td className="px-4 py-3">
        <Badge variant={u.is_active ? "online" : "offline"}>
          {u.is_active ? "active" : "suspended"}
        </Badge>
      </td>
      <td className="px-4 py-3 text-right">
        {u.is_active ? (
          <Button
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={async () => {
              if (!confirm(`Suspend ${u.email}? Their sessions are revoked.`))
                return;
              await suspend.mutateAsync(u.id);
            }}
          >
            Suspend
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() => reactivate.mutate(u.id)}
          >
            Reactivate
          </Button>
        )}
      </td>
    </tr>
  );
}

export function UsersPage() {
  const { data, isLoading, isError } = useUsers();
  // Dual-audience: a super-admin in Platform View (no tenant chosen) cannot
  // list users — the backend requires ?tenant_id (Option-A). Prompt them to
  // pick a tenant rather than surfacing the raw 400.
  const isSuperAdmin = useTenantStore((s) => s.isSuperAdmin);
  const currentTenantId = useTenantStore((s) => s.currentTenantId);
  const needsTenant = isSuperAdmin && !currentTenantId;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Users</h1>
          <p className="text-sm text-muted-foreground">
            {data ? `${data.total} users` : "Loading…"}
          </p>
        </div>
        <div className="flex gap-2">
          <ExportCsvButton
            rows={data?.items ?? []}
            filename="users"
            columns={[
              { header: "Name", value: (r) => r.full_name ?? "" },
              { header: "Email", value: (r) => r.email },
              { header: "Role", value: (r) => r.role ?? "" },
              { header: "Active", value: (r) => (r.is_active ? "yes" : "no") },
              { header: "Last login", value: (r) => r.last_login_at ?? "" },
            ]}
          />
          <Button asChild>
            <Link to="/users/invite">
              <PlusIcon className="mr-2 h-4 w-4" />
              Invite user
            </Link>
          </Button>
        </div>
      </div>

      {needsTenant && (
        <p className="text-sm text-muted-foreground">
          Select a tenant from the switcher to manage its users.
        </p>
      )}
      {!needsTenant && isLoading && <p className="text-sm">Loading…</p>}
      {!needsTenant && isError && (
        <p className="text-sm text-destructive">Could not load users.</p>
      )}

      {!needsTenant && data && data.items.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 text-left">Name</th>
                  <th className="px-4 py-3 text-left">Email</th>
                  <th className="px-4 py-3 text-left">Role</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((u) => (
                  <UserRow key={u.id} u={u} />
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {!needsTenant && data && data.items.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No users yet. Use “Invite user” to send a one-time invite link.
        </p>
      )}
    </div>
  );
}
